import React, { useMemo } from "react"
import { Button, Collapse } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import { defaultTablesFor } from "../../model/defaults"
import { describeFinding, Finding, tablesForFinding } from "../../model/reconcile"
import { shownNames } from "../../model/shownLabels"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import { dataNotReady } from "../AddTable/dataState"

type GroupKey = "new" | "changed" | "data" | "banner"

const GROUP_TITLE: Record<GroupKey, string> = {
  new: "New questions and rows",
  changed: "Changed questions",
  data: "Data",
  banner: "Banner",
}
const GROUP_ORDER: GroupKey[] = ["new", "changed", "data", "banner"]

/** A variable from the data is never a questionnaire change, even in a banner. */
const groupOf = (f: Finding): GroupKey =>
  f.kind === "data-variable-gone" || f.kind === "untabled-data"
    ? "data"
    : f.kind === "new-question" || f.kind === "new-instance"
      ? "new"
      : "bannerId" in f && f.bannerId
        ? "banner"
        : "changed"

/**
 * What the review of the spec against the questionnaire found, grouped:
 * questions and rows that have no table yet (each can be added, or all at
 * once), questions that changed under a table, variables from the data the
 * prepared data no longer has (for a table, a banner group or a filter), and
 * the banner. In a project without a questionnaire the variables of the data
 * that have no table are one finding, under Data: "Choose tables" opens
 * "Build tables from the data" on them (`onChooseTables`). It stays while
 * they have no table. Hidden when there is nothing to review.
 */
const FindingsStrip: React.FC<{ onChooseTables?: (variables: string[]) => void }> = ({
  onChooseTables,
}) => {
  const findings = useTabSpecStore((s) => s.findings)
  const document = useTabSpecStore((s) => s.document)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const addTables = useTabSpecStore((s) => s.addTables)
  const select = useTabSpecStore((s) => s.select)

  // Each finding names what it is about as the tables show it.
  const names = useMemo(() => shownNames(document.labels), [document.labels])
  const groups = useMemo(
    () =>
      GROUP_ORDER.map((key) => ({
        key,
        findings: findings.filter((f) => groupOf(f) === key),
      })).filter((g) => g.findings.length > 0),
    [findings]
  )

  if (findings.length === 0) return null

  const add = (list: Finding[]): void => {
    if (!catalogue) return
    addTables(list.flatMap((f) => tablesForFinding(f, document, catalogue)))
  }

  const count = findings.length
  const body = (
    <div className="ts-findings__body">
      {groups.map((g) => (
        <section
          key={g.key}
          className="ts-findings__group"
          aria-label={GROUP_TITLE[g.key]}
        >
          <div className="ts-findings__heading">
            <h3>{GROUP_TITLE[g.key]}</h3>
            {g.key === "new" && (
              <Button size="small" disabled={!catalogue} onClick={() => add(g.findings)}>
                Add all
              </Button>
            )}
          </div>
          <ul>
            {g.findings.map((f, i) => (
              <li key={i} className="ts-findings__item">
                <span>{describeFinding(f, names)}</span>
                {"tableId" in f && f.tableId && (
                  <Button
                    type="link"
                    size="small"
                    className="ts-link"
                    onClick={() => select(f.tableId ?? null)}
                  >
                    Show table
                  </Button>
                )}
                {g.key === "new" && (
                  <Button size="small" disabled={!catalogue} onClick={() => add([f])}>
                    Add
                  </Button>
                )}
                {f.kind === "untabled-data" && (
                  <Button
                    size="small"
                    disabled={!onChooseTables}
                    onClick={() => onChooseTables?.(f.variables)}
                  >
                    Choose tables
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )

  return (
    <Collapse
      size="small"
      className="ts-findings"
      defaultActiveKey={["findings"]}
      items={[
        {
          key: "findings",
          label: `${count} ${count === 1 ? "thing" : "things"} to review`,
          children: body,
        },
      ]}
    />
  )
}

const NO_QUESTIONNAIRE =
  "This project has no questionnaire. Its tables are built from the variables of the data."

/**
 * Stands in for the grid while the spec has no table: one button adds the
 * default tables of every question of the questionnaire. In a project
 * without a questionnaire the button opens "Build tables from the data"
 * (`onBuildFromData`), once the data's variables are known; until then it
 * says why not.
 */
export const EmptySpec: React.FC<{ onBuildFromData?: () => void }> = ({
  onBuildFromData,
}) => {
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const storeDataOnly = useTabSpecStore((s) => s.dataOnly)
  const status = useTabSpecStore((s) => s.session.status)
  const sessionError = useTabSpecStore((s) => s.session.error)
  const defaults = useTabSpecStore((s) => s.document.defaults)
  const addTables = useTabSpecStore((s) => s.addTables)
  if (storeDataOnly || catalogue?.dataOnly) {
    const notReady = dataNotReady(status, sessionError, catalogue)
    return (
      <div className="ts-empty">
        <div>
          <EmptyState message="No tables yet" subtitle={NO_QUESTIONNAIRE} />
        </div>
        <DataQButton
          type="primary"
          isDisabled={notReady !== null || !onBuildFromData}
          onClick={() => onBuildFromData?.()}
        >
          Build the tables from the data
        </DataQButton>
        {notReady && <p className="ts-hint">{notReady}</p>}
      </div>
    )
  }
  return (
    <div className="ts-empty">
      <div>
        <EmptyState message="No tables yet" />
      </div>
      <DataQButton
        type="primary"
        isDisabled={!catalogue}
        onClick={() =>
          catalogue &&
          addTables(catalogue.questions.flatMap((q) => defaultTablesFor(q, defaults)))
        }
      >
        Build the tables from the questionnaire
      </DataQButton>
    </div>
  )
}

export default FindingsStrip
